import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useRaceStore } from '../../stores/raceStore';
import { useTranslation } from '../../utils/i18n';
import { XIcon, AlertTriangleIcon } from 'lucide-react';
import { CheckpointType, TerrainType } from '../../types';
interface AddSplitDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialKm?: number;
}
const CHECKPOINT_KEYS: CheckpointType[] = ['None', 'BV', 'Aid', 'Water', 'Other'];
const TERRAIN_KEYS: TerrainType[] = ['Road', 'SmoothTrail', 'MixedTrail', 'Rocky', 'Technical', 'MudSand', 'OfftrailSteep'];
export function AddSplitDialog({
  isOpen,
  onClose,
  initialKm
}: AddSplitDialogProps) {
  const {
    t
  } = useTranslation();
  const {
    gpxData,
    addSplitAtKm,
    updateSegment,
    segments
  } = useRaceStore();
  const [km, setKm] = useState<string>(initialKm ? initialKm.toString() : '');
  const [name, setName] = useState<string>('');
  const [checkpointType, setCheckpointType] = useState<CheckpointType>('Aid');
  const [terrainType, setTerrainType] = useState<TerrainType>('SmoothTrail');
  const [cutoffHours, setCutoffHours] = useState<string>('');
  const [cutoffMinutes, setCutoffMinutes] = useState<string>('');
  const [error, setError] = useState('');
  useEffect(() => {
    if (initialKm) setKm(initialKm.toFixed(1));
  }, [initialKm]);
  useEffect(() => {
    if (isOpen) {
      setName('');
      setCheckpointType('Aid');
      setTerrainType('SmoothTrail');
      setCutoffHours('');
      setCutoffMinutes('');
      setError('');
    }
  }, [isOpen]);
  if (!isOpen || !gpxData) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const val = parseFloat(km);
    if (isNaN(val) || val <= 0 || val >= gpxData.totalDistance) {
      setError(t('split.distanceError', {
        max: gpxData.totalDistance.toFixed(1)
      }));
      return;
    }
    // 1. Add the split (creates new segments)
    const splitName = name.trim() || undefined;
    addSplitAtKm(val, splitName);
    // 2. Find the newly created segment to update its properties
    // The new segment will be the one that ends at our split point (val)
    // We need to fetch fresh state because addSplitAtKm just updated it
    const currentSegments = useRaceStore.getState().segments;
    // Find segment where endPointIndex corresponds to the split distance
    // Since we don't have direct index mapping here, we can find the segment
    // whose cumulative distance is closest to 'val'
    let cumulativeDist = 0;
    let targetSegmentId: string | null = null;
    // Simple heuristic: find the segment that ends closest to our split km
    // The segment we just added is the one that *ends* at the split point
    // But wait, segments in store are just segments.
    // The segment ending at 'val' is the one we want to configure.
    // Let's look for the segment that has the name we just set (if provided)
    // or falls in the right place.
    // More robust: The segment we want is the one where `distance` + `start_dist` ≈ `val`
    // Let's iterate and track cumulative distance
    for (const seg of currentSegments) {
      cumulativeDist += seg.distance;
      // Allow small float error margin
      if (Math.abs(cumulativeDist - val) < 0.05) {
        targetSegmentId = seg.id;
        break;
      }
    }
    if (targetSegmentId) {
      const cutoffTotalMinutes = (parseInt(cutoffHours) || 0) * 60 + (parseInt(cutoffMinutes) || 0);
      updateSegment(targetSegmentId, {
        checkpointType,
        terrainType,
        cutoffMinutes: cutoffTotalMinutes > 0 ? cutoffTotalMinutes : undefined
      });
    }
    setKm('');
    setName('');
    setError('');
    onClose();
  };
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" data-id="element-72">
      <Card className="w-full max-w-md shadow-xl animate-in fade-in zoom-in-95 duration-200" data-id="element-73">
        <CardHeader className="flex flex-row items-center justify-between pb-2" data-id="element-74">
          <CardTitle data-id="element-75">{t('split.title')}</CardTitle>
          <button onClick={onClose} className="text-[#9B9BA7] hover:text-[#2D2D2D] p-1 rounded-lg hover:bg-[#F7F7FA] transition-colors" data-id="element-76">
            <XIcon className="h-5 w-5" data-id="element-77" />
          </button>
        </CardHeader>
        <CardContent data-id="element-78">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-79">
            <p className="text-sm text-[#6B6B76]" data-id="element-80">
              {t('split.totalCourse')}{' '}
              <span className="text-[#2D2D2D] font-medium" data-id="element-81">
                {gpxData.totalDistance.toFixed(1)} km
              </span>
            </p>

            {/* Row 1: Distance & Name */}
            <div className="grid grid-cols-2 gap-3" data-id="element-82">
              <Input label={t('split.distance')} type="number" step="0.1" value={km} onChange={e => setKm(e.target.value)} placeholder={t('split.distancePlaceholder')} autoFocus error={error} data-id="element-83" />
              <Input label={t('split.name')} type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t('split.namePlaceholder')} data-id="element-84" />
            </div>

            {/* Row 2: Type & Terrain */}
            <div className="grid grid-cols-2 gap-3" data-id="element-85">
              <div data-id="element-86">
                <label className="block text-sm font-medium text-[#2D2D2D] mb-1.5" data-id="element-87">
                  {t('segments.type')}
                </label>
                <select className="w-full h-10 px-3 rounded-xl border border-[#E8E8ED] bg-white text-sm focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent transition-all appearance-none" value={checkpointType} onChange={e => setCheckpointType(e.target.value as CheckpointType)} data-id="element-88">
                  {CHECKPOINT_KEYS.map(key => <option key={key} value={key} data-id="element-89">
                      {t(`checkpoint.${key}`)}
                    </option>)}
                </select>
              </div>
              <div data-id="element-90">
                <label className="block text-sm font-medium text-[#2D2D2D] mb-1.5" data-id="element-91">
                  {t('segments.terrain')}
                </label>
                <select className="w-full h-10 px-3 rounded-xl border border-[#E8E8ED] bg-white text-sm focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent transition-all appearance-none" value={terrainType} onChange={e => setTerrainType(e.target.value as TerrainType)} data-id="element-92">
                  {TERRAIN_KEYS.map(key => <option key={key} value={key} data-id="element-93">
                      {t(`terrain.${key}`)}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Row 3: Cutoff */}
            <div data-id="element-94">
              <label className="block text-sm font-medium text-[#2D2D2D] mb-1.5 flex items-center gap-2" data-id="element-95">
                {t('segments.cutoff')}{' '}
                <span className="text-[#9B9BA7] font-normal text-xs" data-id="element-96">
                  (optional)
                </span>
              </label>
              <div className="flex items-center gap-2 bg-[#FFFBEB] border border-[#FEF3C7] border-l-4 border-l-[#FF9500] rounded-xl px-3 py-2" data-id="element-97">
                <AlertTriangleIcon className="h-4 w-4 text-[#FF9500]" data-id="element-98" />
                <input type="number" min="0" max="99" value={cutoffHours} onChange={e => setCutoffHours(e.target.value)} placeholder="--" className="w-12 bg-transparent border-0 p-0 text-sm font-bold text-center text-[#2D2D2D] focus:ring-0 focus:outline-none placeholder:text-[#9B9BA7]" data-id="element-99" />
                <span className="text-[#9B9BA7] font-semibold text-xs" data-id="element-100">h</span>
                <input type="number" min="0" max="59" value={cutoffMinutes} onChange={e => setCutoffMinutes(e.target.value)} placeholder="--" className="w-12 bg-transparent border-0 p-0 text-sm font-bold text-center text-[#2D2D2D] focus:ring-0 focus:outline-none placeholder:text-[#9B9BA7]" data-id="element-101" />
                <span className="text-[#9B9BA7] font-semibold text-xs" data-id="element-102">m</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2" data-id="element-103">
              <Button type="button" variant="ghost" onClick={onClose} data-id="element-104">
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" data-id="element-105">
                {t('split.add')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>;
}